import { useMemo, useState, useEffect } from "react";
import {
  FileSignature, FileText, Landmark, AtSign, Facebook as FacebookIcon, Globe,
  ShieldCheck, CreditCard, Users as UsersIcon, Rocket, Coins, Tag, Shirt, Info,
} from "lucide-react";
import {
  ContractDetailsSection,
  FranchiseNameSection,
  FlexPaymentOptionSection,
  LaunchTerritoryConfirmationSection,
  EmailAccountSection,
  FacebookSetupSection,
  WebsiteListingSection,
  DBSSection,
  DirectDebitSection,
  ComfortZoneSection,
  ApronColourSection,
} from "@/components/launch-portal/LaunchPortalSections";
import LaunchPortalContractSection from "@/components/launch-portal/LaunchPortalContractSection";

/**
 * LaunchPortalOverlay — self-contained "YOUR LAUNCH PORTAL" surface
 * that renders below the Discovery Portal on the same URL once HQ
 * has finalised the launch portal.
 *
 * Deliberately encapsulates its own navigation state and section
 * content so the parent DiscoveryPortalPage only needs to:
 *   1) destructure ``launch_portal`` off the /public/discovery payload,
 *   2) render <LaunchPortalOverlay launchPortal slug reload /> once,
 * and doesn't need any structural changes to its existing nav, layout
 * or renderSection switch. Everything Discovery-side stays exactly
 * as it is in production today.
 */
const SECTIONS = [
  // CONTRACT DETAILS — collects the personal information HQ uses to
  // prepare the franchise agreement. Data lives on
  // ``launch_portals.contract_details`` and is locked once submitted.
  // Kept as its own nav pill + panel, ordered FIRST per Sam's brief.
  { id: "contract-details", label: "Contract Details",     icon: FileText,      tone: { text: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-200", activeBg: "bg-indigo-100", activeRing: "ring-indigo-500" } },
  // CONTRACT — the token-gated signing surface for the actual issued
  // agreement (introduced in [21]). Distinct from CONTRACT DETAILS
  // above: this reads live from the ``contracts`` collection and
  // dispatches through the shared <ContractReviewAndSign> component.
  { id: "contract",  label: "Contract",                   icon: FileSignature, tone: { text: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-200", activeBg: "bg-indigo-100", activeRing: "ring-indigo-500" } },
  { id: "franchise-name", label: "Your Franchise Name",   icon: Tag,           tone: { text: "text-violet-600", bg: "bg-violet-50", ring: "ring-violet-200", activeBg: "bg-violet-100", activeRing: "ring-violet-500" } },
  // Flex Payment Option — only surfaced when HQ has toggled
  // ``show_flex_launch`` ON for this franchisee (see the filter
  // applied to ``SECTIONS`` inside the component below). The section
  // renders the two Flex PDFs via ``PortalPdfViewer`` — same viewer
  // as the Discovery Portal, same R2 source of truth.
  { id: "flex",      label: "Payment Options",            icon: Coins,         tone: { text: "text-fuchsia-600", bg: "bg-fuchsia-50", ring: "ring-fuchsia-200", activeBg: "bg-fuchsia-100", activeRing: "ring-fuchsia-500" } },
  // Feb 2026 — nav label + section heading renamed from "Territory
  // Preparation" to "Lock Territory" (owner directive). The label is
  // used both in the nav pill AND rendered as <h3>{activeCfg.label}</h3>
  // in the panel body, so changing it here updates both places.
  { id: "territory", label: "Lock Territory",             icon: Landmark,      tone: { text: "text-teal-600",   bg: "bg-teal-50",   ring: "ring-teal-200",   activeBg: "bg-teal-100",   activeRing: "ring-teal-500"   } },
  { id: "email",     label: "Email Account",              icon: AtSign,        tone: { text: "text-sky-600",    bg: "bg-sky-50",    ring: "ring-sky-200",    activeBg: "bg-sky-100",    activeRing: "ring-sky-500"    } },
  { id: "facebook",  label: "Facebook",                    icon: FacebookIcon,  tone: { text: "text-blue-600",   bg: "bg-blue-50",   ring: "ring-blue-200",   activeBg: "bg-blue-100",   activeRing: "ring-blue-500"   } },
  { id: "website",   label: "Web Profile",                 icon: Globe,         tone: { text: "text-cyan-600",   bg: "bg-cyan-50",   ring: "ring-cyan-200",   activeBg: "bg-cyan-100",   activeRing: "ring-cyan-500"   } },
  { id: "dbs",       label: "DBS Application",            icon: ShieldCheck,   tone: { text: "text-amber-700",  bg: "bg-amber-50",  ring: "ring-amber-200",  activeBg: "bg-amber-100",  activeRing: "ring-amber-500"  } },
  { id: "dd",        label: "Direct Debit",               icon: CreditCard,    tone: { text: "text-lime-700",   bg: "bg-lime-50",   ring: "ring-lime-200",   activeBg: "bg-lime-100",   activeRing: "ring-lime-500"   } },
  { id: "comfort",   label: "Comfort Zone",               icon: UsersIcon,     tone: { text: "text-pink-600",   bg: "bg-pink-50",   ring: "ring-pink-200",   activeBg: "bg-pink-100",   activeRing: "ring-pink-500"   } },
  { id: "apron",     label: "Apron Colour",               icon: Shirt,         tone: { text: "text-rose-600",   bg: "bg-rose-50",   ring: "ring-rose-200",   activeBg: "bg-rose-100",   activeRing: "ring-rose-500"   } },
];

/**
 * Compute the visible section list for a given ``launchPortal`` payload.
 * Exposed so ancestors (e.g. DiscoveryPortalPage) can render a shared
 * mobile nav strip inside their own sticky header without having to
 * duplicate the Flex-toggle filter.
 */
export function useLaunchPortalSections(launchPortal) {
  return useMemo(
    () => SECTIONS.filter((s) => s.id !== "flex" || !!launchPortal?.show_flex),
    [launchPortal?.show_flex],
  );
}

export default function LaunchPortalOverlay({
  launchPortal,
  slug,
  reload,
  // Optional lifted state — when the ancestor mounts a swap-in nav
  // strip in its own sticky header (mobile), it passes ``active`` +
  // ``setActive`` here so both nav copies share a single source of
  // truth. When omitted the component keeps its original internal
  // state (unchanged behaviour on desktop / standalone usage).
  active: activeExternal,
  setActive: setActiveExternal,
}) {
  const sections = useLaunchPortalSections(launchPortal);
  const [activeInternal, setActiveInternal] = useState(sections[0].id);
  const active = activeExternal ?? activeInternal;
  const setActive = setActiveExternal ?? setActiveInternal;
  const activeCfg = sections.find((s) => s.id === active) || sections[0];

  // Feb-2026 owner directive: do NOT auto-scroll when a Launch
  // section pill is tapped. Previous revisions scrolled the overlay
  // (or the section panel) into view, but that fought with the
  // sticky Discovery header — tapping ``Your Franchise Name`` or
  // ``DBS Application`` was hiding the "Your Launch Portal" title
  // or the section heading. The sticky Discovery toggle + the
  // inline Launch nav stay visible regardless of scroll, so the
  // user is already looking at the nav they just tapped; letting
  // the panel re-render in place is the least disruptive behaviour.

  return (
    <section
      className="mt-10 md:mt-14 border-t border-stone-200 bg-stone-50"
      data-testid="launch-portal-overlay"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <header className="flex items-center gap-2 mb-6" data-testid="launch-portal-overlay-heading">
          <Rocket className="w-5 h-5 text-stone-950" />
          <h2 className="font-display text-xl md:text-2xl text-stone-950">Your Launch Portal</h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
          {/* Vertical section picker (desktop) / two-row grid (mobile).
              Mobile behaviour (Feb 2026 — owner directive):
                * Two rows of chips, NOT a horizontal scroller — every
                  section is reachable without swiping.
                * Mobile stickiness is handled by the ancestor
                  (DiscoveryPortalPage) which swaps its own Discovery
                  nav strip out for a copy of ``LaunchPortalMobileNavStrip``
                  the moment the Launch Portal section reaches the
                  top of the viewport. The inline copy below is left
                  in the normal flow so the very first sighting of
                  the nav (as the user scrolls in) still renders
                  underneath the "Your Launch Portal" heading. */}
          <nav className="md:sticky md:top-32 self-start">
            {/* Mobile: two-row grid — 5 cols × 2 rows covers all 10
                possible sections (contract, franchise-name, flex,
                territory, email, facebook, website, dbs, dd, comfort).
                When Flex is off there are 9 items → last row has 4. */}
            <LaunchPortalMobileNavStrip
              sections={sections}
              active={active}
              setActive={setActive}
              className="md:hidden"
              testId="launch-portal-mobile-nav"
            />
            {/* Desktop: vertical list */}
            <div className="hidden md:block space-y-2">
              {sections.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActive(s.id)}
                    data-testid={`launch-portal-nav-desktop-${s.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ring-1 ${
                      isActive ? `${s.tone.activeBg} ${s.tone.activeRing}` : `bg-white ring-stone-200 hover:${s.tone.bg}`
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${s.tone.bg} ${s.tone.text} shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider leading-tight ${isActive ? "text-stone-900" : "text-stone-700"}`}>
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          <main className="min-w-0">
            <div className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6" data-testid={`launch-portal-section-${active}`}>
              {/* Feb-2026 owner brief: readable mobile-only viewing-
                  advice banner at the top of EVERY launch portal
                  section. Hidden at md+. */}
              <div
                className="md:hidden mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900"
                role="status"
                data-testid={`launch-portal-mobile-viewing-advice-${active}`}
              >
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
                <span>
                  Although you can use this portal on a mobile device, it is best viewed on a tablet or desktop computer.
                </span>
              </div>
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-stone-200">
                <activeCfg.icon className={`w-5 h-5 ${activeCfg.tone.text}`} />
                <h3 className="font-display text-lg text-stone-950">{activeCfg.label}</h3>
              </div>
              {renderSectionContent({ active, launchPortal, slug, reload })}
            </div>
          </main>
        </div>
      </div>
    </section>
  );
}

function renderSectionContent({ active, launchPortal, slug, reload }) {
  switch (active) {
    case "contract-details":
      return <ContractDetailsSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "contract":
      return <LaunchPortalContractSection slug={slug} onSigned={reload} />;
    case "franchise-name":
      return <FranchiseNameSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "flex":
      return <FlexPaymentOptionSection slug={slug} launchPortal={launchPortal} onRefresh={reload} />;
    case "territory":
      return <LaunchTerritoryConfirmationSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "email":
      return <EmailAccountSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "facebook":
      return <FacebookSetupSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "website":
      return <WebsiteListingSection />;
    case "dbs":
      return <DBSSection slug={slug} launchPortal={launchPortal} docTypes={launchPortal?.dbs_doc_types} onSubmitted={reload} />;
    case "dd":
      return <DirectDebitSection slug={slug} launchPortal={launchPortal} onSubmitted={reload} />;
    case "comfort":
      return <ComfortZoneSection launchPortal={launchPortal} />;
    case "apron":
      return <ApronColourSection launchPortal={launchPortal} slug={slug} onRefresh={reload} />;
    default:
      return null;
  }
}

/**
 * Two-row mobile nav strip for the Launch Portal.
 *
 * Rendered inline by ``LaunchPortalOverlay`` AND (optionally) re-mounted
 * inside the Discovery Portal's sticky top header the moment the user
 * scrolls past the Discovery section, so the sticky icons "swap" from
 * Discovery → Launch cleanly instead of stacking on top of each other.
 * The two copies share ``active`` state via the props lifted up to
 * ``DiscoveryPortalPage``, so tapping a chip in either copy switches
 * the Launch Portal section immediately.
 */
export function LaunchPortalMobileNavStrip({
  sections,
  active,
  setActive,
  className = "",
  testId = "launch-portal-mobile-nav",
}) {
  return (
    <div
      className={`grid grid-cols-5 gap-1 pb-1 pt-1 px-1 bg-stone-50/95 backdrop-blur border-b border-stone-200 ${className}`}
      data-testid={testId}
    >
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => setActive(s.id)}
            data-testid={`${testId}-item-${s.id}`}
            className={`flex flex-col items-center gap-0.5 px-0.5 py-1 rounded-lg ring-1 transition-colors ${
              isActive ? `${s.tone.activeBg} ${s.tone.activeRing}` : `${s.tone.bg} ring-stone-200`
            }`}
          >
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full bg-white ${s.tone.text}`}>
              <Icon className="w-3 h-3" />
            </span>
            <span
              className={`text-[9px] font-bold uppercase tracking-tight leading-tight text-center break-words ${
                isActive ? "text-stone-900" : "text-stone-600"
              }`}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
